import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  acceptPartnership,
  deletePartnership,
  PartnershipView,
  refusePartnership,
} from "../../../api/partnership";
import { enumsectionToI18nKey } from "../../../generated/enums";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { isAssistant } from "../../../utils/roles";
import { usePartnerships } from "../../../hooks/usePartnerships";
import { DeleteModal } from "../../common/DeleteModal";
import { Header } from "../../Header";
import { PageTour } from "../../tour/PageTour";
import { partnershipsTourSteps } from "../../tour/steps/pageSteps";
import { sectionToIcon } from "../../type/default";
import { AssignCollaboratorsModal } from "./AssignCollaboratorsModal";
import { RequestPartnershipModal } from "./RequestPartnershipModal";
import { SharePartnershipModal } from "./SharePartnershipModal";

/**
 * Page PARTENAIRES.
 *
 * Trois blocs, dans cet ordre d'importance :
 *  1. les demandes à traiter (celles où le backend a mis `can_respond`) ;
 *  2. les partenaires actifs (`accepted`) — la réponse à « avec qui suis-je lié ? » ;
 *  3. les demandes que j'ai envoyées et qui attendent une réponse.
 *
 * Les refus ne sont pas affichés : ils encombreraient l'écran sans rien apporter (un refus
 * peut être suivi d'une nouvelle demande, l'index partiel côté base l'autorise).
 */

/**
 * Libellé du lien du point de vue de l'utilisateur : suis-je le client ou le prestataire ?
 *
 * Renvoie un libellé neutre pour MES demandes en attente : le backend masque alors le nom et
 * le type de la cible (anti-énumération), il n'y a donc rien à qualifier.
 */
const relationLabel = (p: PartnershipView): string => {
  if (!p.company?.company_name) return "Destinataire";
  if (p.role === "client") {
    return p.partnership_type === "installer"
      ? "Votre installateur"
      : "Votre groupement";
  }
  return "Votre client";
};

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

type PartnershipCardProps = {
  partnership: PartnershipView;
  onRespond?: (id: number, accept: boolean) => void;
  onDelete?: (partnership: PartnershipView) => void;
  onShare?: (partnership: PartnershipView) => void;
  onAssign?: (partnership: PartnershipView) => void;
  busy?: boolean;
};

const PartnershipCard = ({
  partnership,
  onRespond,
  onDelete,
  onShare,
  onAssign,
  busy,
}: PartnershipCardProps) => {
  // Demande que J'AI émise : le backend ne renvoie ni nom ni type tant qu'elle est en
  // attente, pour ne pas confirmer que l'email saisi correspond à un compte existant.
  const isAnonymous = !partnership.company?.company_name;

  return (
    <div className="bg-white border border-grey-medium rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0">
        <div
          className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
            isAnonymous
              ? "bg-grey-light text-tertiary"
              : "bg-secondary text-tertiary"
          }`}
        >
          <FontAwesomeIcon icon={isAnonymous ? "clock" : "handshake"} />
        </div>
        <div className="min-w-0">
          <p className="font-oxaniumMedium text-default-text truncate">
            {isAnonymous
              ? "Demande envoyée"
              : partnership.company.company_name}
          </p>
          <p className="text-sm text-tertiary">
            {isAnonymous
              ? `En attente de réponse · Envoyée le ${formatDate(
                  partnership.creation_date
                )}`
              : `${relationLabel(partnership)} · Depuis le ${formatDate(
                  partnership.creation_date
                )}`}
          </p>
        </div>
      </div>

      <div className="flex gap-2 shrink-0 self-end md:self-auto">
        {onRespond && (
          <>
            <button
              type="button"
              onClick={() => onRespond(partnership.id, false)}
              disabled={busy}
              className="px-4 py-2 rounded-lg border border-grey-medium text-tertiary font-oxaniumMedium hover:bg-grey-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon="times" className="mr-2" />
              Refuser
            </button>
            <button
              type="button"
              onClick={() => onRespond(partnership.id, true)}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-secondary text-tertiary font-oxaniumMedium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon="check" className="mr-2" />
              Accepter
            </button>
          </>
        )}

        {onShare && (
          <button
            type="button"
            onClick={() => onShare(partnership)}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-grey-medium text-tertiary font-oxaniumMedium hover:bg-grey-light transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon icon="share-nodes" className="mr-2" />
            Partager
          </button>
        )}

        {onAssign && (
          <button
            type="button"
            onClick={() => onAssign(partnership)}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-grey-medium text-tertiary font-oxaniumMedium hover:bg-grey-light transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon icon="users" className="mr-2" />
            Collaborateurs
          </button>
        )}

        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete(partnership)}
            disabled={busy}
            aria-label="Supprimer ce partenaire"
            title="Supprimer ce partenaire"
            className="px-3 py-2 rounded-lg border border-grey-medium text-tertiary hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon icon="trash-can" />
          </button>
        )}
      </div>
    </div>
  );
};

const Section = ({
  title,
  count,
  children,
  id,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
  /** Ancre du tutoriel de la page (cf. components/tour). */
  id?: string;
}) => {
  if (count === 0) return null;
  return (
    <section id={id} className="mt-6">
      <h2 className="text-lg font-oxaniumMedium text-default-text mb-3">
        {title} ({count})
      </h2>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
};

export const PartnershipsPage = () => {
  const { partnerships, loading, error, reload } = usePartnerships();
  const { getAccountStorage } = useAccountStorage();
  const { t } = useTranslation();
  const accountType = getAccountStorage()?.type;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<PartnershipView | null>(null);
  const [toShare, setToShare] = useState<PartnershipView | null>(null);
  const [toAssign, setToAssign] = useState<PartnershipView | null>(null);

  // Un éleveur cherche un prestataire ; un installateur ou un groupement cherche un client.
  const targetLabel =
    accountType === "breeder"
      ? "votre installateur ou groupement"
      : "votre client éleveur";

  const toRespond = partnerships.filter((p) => p.can_respond);
  const active = partnerships.filter((p) => p.status === "accepted");
  // Mes propres demandes : en attente, mais ce n'est pas à moi de répondre.
  const sent = partnerships.filter(
    (p) => p.status === "pending" && !p.can_respond
  );

  const handleRespond = async (id: number, accept: boolean) => {
    setBusyId(id);
    setActionError(null);
    const result = accept
      ? await acceptPartnership(id)
      : await refusePartnership(id);
    setBusyId(null);

    if (!result.success) {
      setActionError(result.error);
      return;
    }
    await reload();
  };

  const handleConfirmDelete = async () => {
    if (!toDelete) return;
    const id = toDelete.id;
    setToDelete(null);
    setBusyId(id);
    setActionError(null);
    const result = await deletePartnership(id);
    setBusyId(null);

    if (!result.success) {
      setActionError(result.error);
      return;
    }
    await reload();
  };

  return (
    <div className="p-4">
      <Header
        id="tour-partnerships-header"
        title={t(enumsectionToI18nKey["section_partnerships"])}
        icon={sectionToIcon.section_partnerships}
        buttonText="Demander un partenariat"
        onButtonClick={() => setIsModalOpen(true)}
        help={<PageTour id="partnerships" steps={partnershipsTourSteps} />}
      />

      {(actionError || error) && (
        <div className="mt-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
          {actionError || error}
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-tertiary">Chargement…</p>
      ) : (
        <>
          <Section
            id="tour-partnerships-to-respond"
            title="Demandes à traiter"
            count={toRespond.length}
          >
            {toRespond.map((p) => (
              <PartnershipCard
                key={p.id}
                partnership={p}
                onRespond={handleRespond}
                busy={busyId === p.id}
              />
            ))}
          </Section>

          <Section
            id="tour-partnerships-active"
            title="Mes partenaires"
            count={active.length}
          >
            {active.map((p) => (
              <PartnershipCard
                key={p.id}
                partnership={p}
                // Seul le CLIENT du partenariat partage ses ressources : le prestataire
                // les reçoit, il n'a rien à exposer (le backend renvoie 403 sinon).
                onShare={p.role === "client" ? setToShare : undefined}
                // Miroir du partage : le prestataire affecte SES collaborateurs sur ce
                // que le client lui a partagé. Réservé aux types principaux — un
                // collaborateur ne s'affecte pas lui-même (403 côté backend).
                onAssign={
                  p.role === "provider" && !isAssistant(accountType)
                    ? setToAssign
                    : undefined
                }
                onDelete={setToDelete}
                busy={busyId === p.id}
              />
            ))}
          </Section>

          <Section
            id="tour-partnerships-sent"
            title="Demandes envoyées"
            count={sent.length}
          >
            {sent.map((p) => (
              <PartnershipCard
                key={p.id}
                partnership={p}
                onDelete={setToDelete}
                busy={busyId === p.id}
              />
            ))}
          </Section>

          {toRespond.length === 0 &&
            active.length === 0 &&
            sent.length === 0 &&
            !error && (
              <div className="mt-10 text-center text-tertiary">
                <FontAwesomeIcon icon="handshake" size="2x" className="mb-3" />
                <p>Vous n'avez pas encore de partenaire.</p>
                <p className="text-sm mt-1">
                  Demandez un partenariat avec l'email de {targetLabel}.
                </p>
              </div>
            )}
        </>
      )}

      <RequestPartnershipModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          reload();
        }}
        targetLabel={targetLabel}
      />

      <AssignCollaboratorsModal
        isOpen={!!toAssign}
        onClose={() => setToAssign(null)}
        partnershipId={toAssign?.id ?? null}
        companyName={toAssign?.company?.company_name ?? ""}
      />

      <SharePartnershipModal
        isOpen={!!toShare}
        onClose={() => setToShare(null)}
        partnershipId={toShare?.id ?? null}
        companyName={toShare?.company?.company_name ?? ""}
      />

      {/* Double vérification avant rupture : le lien conditionne des accès (produits,
          contacts), une suppression par mégarde se paierait en re-demande + validation. */}
      <DeleteModal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        title={
          toDelete?.company?.company_name
            ? "Supprimer ce partenaire"
            : "Annuler cette demande"
        }
        warningText={
          toDelete?.company?.company_name
            ? `Voulez-vous vraiment rompre le partenariat avec ${toDelete.company.company_name} ? Cette action est irréversible : il faudra une nouvelle demande, acceptée par l'autre partie, pour le rétablir.`
            : "Voulez-vous vraiment annuler cette demande de partenariat ?"
        }
        handleConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
};
